import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider
} from '@mui/material';
import { ContentCopy, Send, Lock, LockOpen } from '@mui/icons-material';
import { usePlan } from '../../hooks/PlanProvider'; // To get selectedPlan details
import { APIClient, APIEndpoint } from '../../lib/api_client'; // For sending invites
import { useSnackbar } from "../../hooks/SnackbarProvider";
import { Plan, SavedPlan, isSavedPlan } from '../../types/interfaces'; // Import SavedPlan and isSavedPlan
import { isValidEmail } from 'src/lib/utils';
import { SocialAnalytics } from '../../lib/analytics';

interface SharePlanDialogProps {
  open: boolean;
  onClose: () => void;
}

const SharePlanDialog: React.FC<SharePlanDialogProps> = ({ open, onClose }) => {
  const { selectedPlan, setSelectedPlan } = usePlan();
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isUpdatingPublicStatus, setIsUpdatingPublicStatus] = useState(false);
  const apiClient = APIClient;
  const { triggerSnackbar } = useSnackbar();

  // Local state for the public toggle, initialized from selectedPlan
  const [isPlanPublic, setIsPlanPublic] = useState(selectedPlan?.isPublic || false);

  useEffect(() => {
    // Reset state when dialog opens/closes or plan changes
    setEmailInput('');
    setEmails([]);
    setInviteError(null);
    // Update local public status if selectedPlan changes
    if (selectedPlan && isSavedPlan(selectedPlan)) {
      setIsPlanPublic(selectedPlan.isPublic || false);
    }
  }, [open, selectedPlan]);

  const handleAddEmail = () => {
    const newEmail = emailInput.trim();
    if (newEmail && !emails.includes(newEmail) && isValidEmail(newEmail)) {
      setEmails([...emails, newEmail]);
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleCopyToClipboard = () => {
    if (selectedPlan && isSavedPlan(selectedPlan)) {
      const shareUrl = (selectedPlan as SavedPlan).shareUrl;
      if (shareUrl) {
        navigator.clipboard.writeText(shareUrl)
          .then(() => {
            triggerSnackbar('Share link copied to clipboard!', 2000, 'success');
            // Track plan share analytics
            SocialAnalytics.planShare(selectedPlan.id, 'link_copy');
          })
          .catch(err => triggerSnackbar('Failed to copy link.', 2000, 'error'));
      }
    }
  };

  const handleSendInvites = async () => {
    if (!selectedPlan || !isSavedPlan(selectedPlan)) return;

    if (emails.length === 0) {
      triggerSnackbar('Please add at least one email address.', 3000, 'error');
      return;
    }

    setIsInviting(true);
    setInviteError(null);
    try {
      await apiClient.post(`/plans/${selectedPlan.id}/invite/`, { emails });
      triggerSnackbar(`Invitations sent to ${emails.length} email(s).`, 3000, 'success');
      
      // Track plan invite analytics
      SocialAnalytics.invite(selectedPlan.id, emails.length);
      
      setEmails([]); // Clear the email list
    } catch (error: any) {
      console.error('Failed to send invites:', error);
      setInviteError(error.response?.data?.detail || error.message || 'Failed to send invitations.');
      triggerSnackbar('Failed to send invitations.', 3000, 'error');
    } finally {
      setIsInviting(false);
    }
  };

  const handleToggleIsPublic = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedPlan || !isSavedPlan(selectedPlan)) return;

    const newPublicStatus = event.target.checked;
    setIsUpdatingPublicStatus(true);
    try {
      const response = await apiClient.patch<{ is_public: boolean }>(`${APIEndpoint}/plans/${selectedPlan.slug}/`, { is_public: newPublicStatus });
      setIsPlanPublic(newPublicStatus);
      // Update the selectedPlan in the context as well
      if (setSelectedPlan && selectedPlan) {
        setSelectedPlan({ ...selectedPlan, isPublic: newPublicStatus });
      }
      triggerSnackbar(`Plan is now ${newPublicStatus ? 'public' : 'private'}.`, 2000, 'success');
      // TODO: Track analytics: plan_privacy_changed (plan_id: selectedPlan.id, is_public: newPublicStatus)
    } catch (error: any) {
      console.error('Failed to update plan public status:', error);
      triggerSnackbar('Failed to update plan status.', 3000, 'error');
      // Revert switch if API call fails
      event.target.checked = !newPublicStatus;
    } finally {
      setIsUpdatingPublicStatus(false);
    }
  };

  if (!selectedPlan || !isSavedPlan(selectedPlan)) {
    // This dialog should not be open if there's no shareable plan selected
    // Or if the plan is not a SavedPlan (which has id and slug)
    return null;
  }

  // At this point, selectedPlan is definitely a SavedPlan
  const plan = selectedPlan as SavedPlan;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share "{plan.name}"</DialogTitle>
      <DialogContent dividers>
        <FormGroup sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isPlanPublic}
                onChange={handleToggleIsPublic}
                disabled={isUpdatingPublicStatus}
                icon={<Lock />}
                checkedIcon={<LockOpen />}
              />
            }
            label={isPlanPublic ? 'Plan is Public' : 'Plan is Private'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
            {isPlanPublic
              ? 'Anyone with the link can view basic plan details. They must log in to RSVP and see full details.'
              : 'Only the owner (and future collaborators/invitees) can view this plan. Make it public to share more broadly.'}
          </Typography>
          {isUpdatingPublicStatus && <CircularProgress size={20} sx={{ ml: 2, mt: 1 }} />}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>Share Link</Typography>
        <Box display="flex" alignItems="center" mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            value={plan.shareUrl || 'Generating link...'}
            InputProps={{ readOnly: true }}
            size="small"
          />
          <IconButton onClick={handleCopyToClipboard} disabled={!plan.shareUrl} sx={{ ml: 1 }}>
            <ContentCopy />
          </IconButton>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Invite by Email</Typography>
        <Box display="flex" alignItems="center" mb={1}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter email address"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
            size="small"
          />
          <Button onClick={handleAddEmail} variant="contained" sx={{ ml: 1, minWidth: 'auto', p: '6px 12px' }} disabled={!emailInput.trim()}>Add</Button>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={1} mb={2} mt={1} minHeight={emails.length > 0 ? 'auto' : 0}>
          {emails.map(email => (
            <Box key={email} sx={{ bgcolor: 'action.hover', p: '4px 8px', borderRadius: '16px', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 0.5 }}>{email}</Typography>
              <IconButton size="small" onClick={() => handleRemoveEmail(email)} sx={{ p: 0.2, fontSize: '0.8rem' }}>&times;</IconButton>
            </Box>
          ))}
        </Box>
        {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSendInvites}
          variant="contained"
          startIcon={isInviting ? <CircularProgress size={20} color="inherit" /> : <Send />}
          disabled={emails.length === 0 || isInviting}
        >
          Send Invites
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharePlanDialog; 